import { getDefectOptions } from 'common/constants';
import {
  TEMPLATE_DEFECT_NAME_TO_ID,
  normalizeDefectNameKey,
} from 'common/constants/defectTypes';

/** Normalized English → suffix under `common.defects.names.*` (built-in rotating catalog). */
const BUILTIN_EN_TO_NAMES_SUFFIX = {
  unbalance: 'unbalance',
  'angular misalignment': 'angularMisalignment',
  'parallel misalignment': 'parallelMisalignment',
  'bent shaft': 'bentShaft',
  'rotating looseness': 'rotatingLooseness',
  'structural looseness': 'structuralLooseness',
  resonance: 'resonance',
  'flow turbulence': 'flowTurbulence',
  cavitation: 'cavitation',
  'blade/vane defect': 'bladeVaneDefect',
  'stage1 defect': 'stage1Defect',
  'stage2 defect': 'stage2Defect',
  'stage3 defect': 'stage3Defect',
  'stage4 defect': 'stage4Defect',
  'tooth wear': 'toothWear',
  'tooth load': 'toothLoad',
  eccentricity: 'eccentricity',
  misalignment: 'misalignment',
  'broken tooth': 'brokenTooth',
  'hunting tooth': 'huntingTooth',
  'worn/loose': 'wornLoose',
  'sheave misalignment': 'sheaveMisalignment',
};

function translationExists(t, key) {
  const value = t(key);
  return value !== key;
}

/**
 * Resolve a human-readable defect label using stable ids and known English catalogs.
 *
 * @param {function} t - i18next `t`
 * @param {object} options
 * @param {number|string|null|undefined} options.defectId - Failure mode / defect id from API
 * @param {string|null|undefined} options.defectName - Fallback display name (often English)
 * @param {Array<{id:number,name:string}>} [options.failureModesList=[]] - Account failure modes from Redux
 * @returns {string}
 */
export function getDefectDisplayName(t, { defectId, defectName, failureModesList = [] }) {
  const parsedId =
    defectId !== undefined && defectId !== null && defectId !== '' && defectId !== 0
      ? Number(defectId)
      : NaN;
  const hasNumericId = Number.isFinite(parsedId) && parsedId > 0;

  if (hasNumericId) {
    const catalog = getDefectOptions(t);
    const builtin = catalog[parsedId];
    if (builtin && builtin.name) {
      return builtin.name;
    }

    const templateKey = `taskTemplates.defectOptions.${parsedId}`;
    if (translationExists(t, templateKey)) {
      return t(templateKey);
    }

    const fm = Array.isArray(failureModesList)
      ? failureModesList.find(f => Number(f.id) === parsedId)
      : null;
    if (fm && fm.name) {
      return resolveDefectNameString(t, fm.name);
    }
  }

  if (defectName) {
    return resolveDefectNameString(t, defectName);
  }

  return '';
}

function resolveDefectNameString(t, name) {
  const raw = String(name);
  const norm = normalizeDefectNameKey(raw);
  if (!norm) return raw;

  const templateId = TEMPLATE_DEFECT_NAME_TO_ID[norm];
  if (templateId != null) {
    const templateKey = `taskTemplates.defectOptions.${templateId}`;
    if (translationExists(t, templateKey)) {
      return t(templateKey);
    }
  }

  const suffix = BUILTIN_EN_TO_NAMES_SUFFIX[norm];
  if (suffix) {
    const nameKey = `common.defects.names.${suffix}`;
    if (translationExists(t, nameKey)) {
      return t(nameKey);
    }
  }

  return raw;
}
